import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { isEqual, pickBy } from 'lodash';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DPFormDataResponse } from '../models/DPFormDataResponse';
import { DPFormField } from '../models/DPFormField';
import { DPUserStatus } from '../models/enums/DPUserStatus';
import { ApiCacheService } from './api-cache.service';
import { ApiUrlService } from './api-url.service';
import { ApplicationService } from './application.service';
import { FormsService } from './forms.service';
import { LoggingService } from './logging.service';
import { MultiStepFormServiceInterface } from './multi-step-form.service.interface';

export interface FormStep {
  label: string;
  fields: DPFormField[];
  savedResult: Record<string, any>;
  form: UntypedFormGroup;
}

/**
 * Multi-step form management service used in Form Step page.
 */
@Injectable({
  providedIn: 'root',
})
export class MultiStepFormService implements MultiStepFormServiceInterface {
  private formData: DPFormDataResponse;
  private allSteps: FormStep[] = [];

  // Current step index
  public stepIndex = new BehaviorSubject(0);
  // Current step object
  public step = new BehaviorSubject<FormStep | null>(null);

  constructor(
    private logging: LoggingService,
    private apiCache: ApiCacheService,
    private applicationService: ApplicationService,
    private formsService: FormsService,
    private urls: ApiUrlService
  ) {}

  /**
   * Initialize service with data and steps. Steps are created in FormStep component
   * due to its dependency to unsubscribe on Component Destroy.
   */
  setFormDataAndSteps(data: DPFormDataResponse, steps: FormStep[]) {
    this.formData = data;
    this.allSteps = steps;
  }

  isLastStep() {
    return this.stepIndex.value === this.allSteps.length - 1;
  }

  isFirstStep() {
    return this.stepIndex.value === 0;
  }

  areAllStepsValid() {
    return this.allSteps.every((step) => step.form.valid);
  }

  changeStep(stepIndex: number) {
    this.stepIndex.next(stepIndex);
    this.step.next(this.getStepObject(stepIndex));
  }

  isStepUpdated(stepIndex: number) {
    const { form, savedResult } = this.getStepObject(stepIndex);

    // Empty values need to be filtered out during comparison
    const formValues = pickBy(form.value, (val) => val !== '');
    const savedValues = pickBy(savedResult, (val) => val !== '');

    return !isEqual(formValues, savedValues);
  }

  isCurrentStepUpdated() {
    return this.isStepUpdated(this.stepIndex.value);
  }

  getUpdatedValues(stepIndex: number) {
    const { form, savedResult, fields } = this.getStepObject(stepIndex);

    /**
     * Result should include:
     * - updated values
     * - removed values
     *
     * Untouched fields should be filtered out.
     */
    return fields
      .filter(
        (field) =>
          form.value[field.name] !== savedResult[field.name] &&
          (form.value[field.name] || savedResult[field.name])
      )
      .reduce(
        (values, field) => ({
          ...values,
          [field.name]: form.value[field.name] || '',
        }),
        {}
      );
  }

  /**
   * Generates html content to display invalid steps list.
   */
  getInvalidStepList() {
    return this.allSteps
      .map((step, i) => `Step ${i + 1}. ${step.label}`)
      .filter((_, i) => !this.allSteps[i].form.valid)
      .join('<br/>');
  }

  saveCurrentStep() {
    return this.saveStep(this.stepIndex.value);
  }

  /**
   * Look up field value by name. This is used in Validations.
   *
   * @param fieldName
   * @returns
   */
  lookupFieldValue(fieldName: number | string) {
    const fn = fieldName.toString();
    for (const step of this.allSteps) {
      const field = step.fields.find((field) => field.name === fn);
      if (field) {
        return step.form.value[fn];
      }
    }
    return null;
  }

  /**
   * Save step answers.
   */
  saveStep(stepIndex: number): Observable<number> {
    const step = this.getStepObject(stepIndex);

    if (!step) {
      return throwError(
        new Error('Failed to save step. Step is not available.')
      );
    }

    if (!this.isStepUpdated(stepIndex)) {
      return of(stepIndex);
    }

    const updatedValues = this.getUpdatedValues(stepIndex);

    return this.formsService
      .saveFormAnswers(
        this.applicationService.userForm.userFormID,
        updatedValues
      )
      .pipe(
        switchMap(() => {
          /**
           * Updating completed form values will reset the userForm status to progress
           * because updated form needs to be signed again.
           *
           * So if the user is updating completed form, it needs to refresh current form
           * and user form status.
           */
          if (
            this.applicationService.userForm.userStatusID ===
            DPUserStatus.Completed
          ) {
            return this.applicationService.refreshFormAndUserForm();
          }

          return of(stepIndex);
        }),
        tap(() => {
          this.logging.info('Form Answers Saved');

          /**
           * This is for optimistic UI update.
           *
           * All api calls get values from cache if one exists.
           * Here, application cache for application form data is updated instead of
           * fetching it again from server.
           */
          try {
            if (this.formData.pages[stepIndex]) {
              this.formData.pages[stepIndex].questions.forEach((question) => {
                question.userAnswer = [
                  { value: '' + step.form.value[question.pageQuestionID] },
                ];
              });
            }
            this.apiCache.updateCache(
              this.urls.userFormDetails(
                this.applicationService.userForm.userFormID
              ),
              this.formData
            );
          } catch (err) {
            console.log(err);
          }

          // Update savedResult of current step.
          step.savedResult = step.form.value;
        })
      ) as Observable<number>;
  }

  /**
   * Get step object with index.
   *
   * @param index
   * @returns
   */
  private getStepObject(index: number) {
    if (!this.allSteps[index]) {
      throw new Error(`Step ${index} doesn't exist`);
    }

    return this.allSteps[index];
  }
}
